import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const SORT_KEY = 'table-sort';

const saveToLocalStorage = ({ value, tab, sort, variant = 'table' }) => {
	const key = `${variant}-${SORT_KEY}`;
	const saved = localStorage.getItem(key);

	let parsed = {};
	try {
		if (saved) parsed = JSON.parse(saved);
	} catch (e) {
		console.warn(`Error getting sort value=${value} tab=${tab} variant=${variant}`, e);
	}

	const next = tab
		? {
				...parsed,
				[value]: {
					...(parsed?.[value] && !Array.isArray(parsed[value]) ? parsed[value] : {}),
					[tab]: sort,
				},
			}
		: {
				...parsed,
				[value]: sort,
			};

	localStorage.setItem(key, JSON.stringify(next));
};

const defaultSort = { field: '', sort: null };
const getSavedSort = ({ value, tab, variant = 'table' }) => {
	const saved = localStorage.getItem(`${variant}-${SORT_KEY}`);

	if (!saved) return defaultSort;

	try {
		const parsed = JSON.parse(saved);

		return tab ? (parsed?.[value]?.[tab] ?? defaultSort) : (parsed?.[value] ?? defaultSort);
	} catch (error) {
		console.error('Error parsing saved sort:', error);
		return defaultSort;
	}
};

const TableSortContext = createContext(null);

export const useTableSort = () => {
	const context = useContext(TableSortContext);

	if (!context) {
		throw new Error('useTableSort must be used within a TableSortContextProvider');
	}

	return context;
};

export const TableSortContextProvider = ({ value = '', tab = '', variant = 'table', children }) => {
	const [sortModel, setSortModelState] = useState(() => {
		const savedSort = getSavedSort({ value, tab, variant });
		if (!savedSort?.field || !savedSort?.sort) return [];

		return [savedSort];
	});

	const key = JSON.stringify({ value, tab, variant });

	useEffect(() => {
		const savedSort = getSavedSort({ value, tab, variant });
		console.log('Saved sort', savedSort);
		setSortModelState(!savedSort?.field || !savedSort?.sort ? [] : [savedSort]);
	}, [key]);

	const setSortModel = useCallback(
		(model = []) => {
			const sort = model?.[0] ?? { field: '', sort: null };

			setSortModelState(model);
			saveToLocalStorage({ value, tab, variant, sort });
		},
		[value, tab, variant],
	);

	const clearSort = useCallback(() => {
		setSortModel([]);
	}, [setSortModel]);

	const currentSort = useMemo(() => sortModel?.[0] ?? null, [sortModel]);

	const context = useMemo(
		() => ({
			sortModel,
			setSortModel,
			clearSort,
			currentSort,
		}),
		[sortModel, setSortModel, clearSort, currentSort],
	);

	return <TableSortContext.Provider value={context}>{children}</TableSortContext.Provider>;
};

TableSortContextProvider.displayName = 'TableSortContextProvider';
